import Vue from 'vue';
import Vuex from 'vuex';
import axios from '../utility/axios';
import router from '../router/index';
import { setCookie, deleteCookie, getCookie } from '../utility/cookie';
import parseJwt from '../utility/jwt';
/* global gtag */

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    loginStep: 'cbLogin',
    deviceTestStep: 'cbDeviceTestFetch',
    deviceTestStartTime: 0,
    manualTestStep: '1',
    screenSize: '',
    formErrorMessage: '',
    downloadMobileAppTitle: 'Get more from MyADT',
    errorModal: {
      body: '',
      tryAgainDestination: '',
      showTroubleshoot: false,
      // showLiveChat: false,
      errorCode: undefined,
    },
    userInfo: {
      hasMyADTCredentials: false,
      manualTestRequired: false,
      sessionToken: null,
      telephoneNumber: null,
      pic: null,
      currentLocation: {},
      locations: [],
    },
    modal: {
      isOpen: false,
      closableOnBackgroundClick: true,
      closableOnEscapeKey: true,
      centeredOnMobile: false,
      modalContent: '',
      returnFocusID: '',
    },
    navigationModal: {
      isOpen: false,
    },
    troubleshoot: {
      buttonText: '',
    },
    requestInProgress: false,
    // isLiveChatReady: false,
    // isLiveChatOpen: false,
    // isLiveChatExpanded: false,
    sessionTimeout: null,
    pollingTimeout: null,
    pollingCounter: 0,
    testFailed: false,
  },
  mutations: {
    updateLoginStep(state, payload) {
      state.loginStep = payload;
    },
    updateDeviceTestStep(state, payload) {
      state.deviceTestStep = payload;
    },
    updateManualTestStep(state, payload) {
      state.manualTestStep = payload;
    },
    setDeviceTestStartTime(state, payload) {
      state.deviceTestStartTime = payload;
    },
    setIsNavigationModalOpen(state, isOpen) {
      if (isOpen && state.screenSize === 'mobile') {
        window.scrollTo(0, document.body.scrollHeight);
      }
      state.navigationModal.isOpen = isOpen;
    },
    openModal(state, payload) {
      const modalContent = { ...payload };
      modalContent.isOpen = true;
      state.modal = modalContent;
    },
    closeModal(state) {
      state.modal.isOpen = false;
    },
    // setIsLiveChatOpen(state, payload) {
    //   state.isLiveChatOpen = payload;
    // },
    // toggleIsLiveChatExpanded(state) {
    //   state.isLiveChatExpanded = !state.isLiveChatExpanded;
    // },
    // setIsLiveChatReady(state) {
    //   state.isLiveChatReady = true;
    // },
    setErrorModalContent(state, payload) {
      state.errorModal = payload;
    },
    updateHasMyADTCredentials(state, payload) {
      state.userInfo.hasMyADTCredentials = payload;
    },
    updateScreenSize(state, payload) {
      state.screenSize = payload;
    },
    updateManualTestRequired(state, payload) {
      state.userInfo.manualTestRequired = payload;
    },
    updateUserLocations(state, locations) {
      state.userInfo.locations = locations;
    },
    updateCurrentLocation(state, currentLocation) {
      const location = state.userInfo.locations[currentLocation];
      state.userInfo.currentLocation = location;
    },
    setSessionToken(state, token) {
      state.userInfo.sessionToken = token;
    },
    updateUserInfo(state, payload) {
      state.userInfo = payload;
    },
    updateFormErrorMessage(state, message) {
      state.formErrorMessage = message;
    },
    updateDownloadMobileAppTitle(state, title) {
      state.downloadMobileAppTitle = title;
    },
    setSessionTimeout(state, payload) {
      state.sessionTimeout = payload;
    },
    clearSessionTimeout(state) {
      clearInterval(state.sessionTimeout);
      state.sessionTimeout = null;
    },
    setPollingTimeout(state, payload) {
      state.pollingTimeout = payload;
    },
    clearPollingTimeout(state) {
      clearTimeout(state.pollingTimeout);
      state.pollingTimeout = null;
      state.deviceTestStartTime = null;
    },
    setPollingCounter(state, payload) {
      state.pollingCounter = payload;
    },
    updateTroubleshootPage(state, payload) {
      state.troubleshoot = payload;
    },
    setRequestInProgress(state, payload) {
      state.requestInProgress = payload;
    },
    setTestFailed(state) {
      state.testFailed = true;
    },
  },
  actions: {
    updateDeviceTestStep: ({ commit }, payload) => {
      commit('updateDeviceTestStep', payload);
    },
    openModal: ({ commit }, payload) => {
      commit('openModal', payload);
    },
    openErrorModal: ({ commit }, payload) => {
      commit('setErrorModalContent', payload);

      commit('openModal', {
        modalContent: 'cbError',
        closableOnBackgroundClick: false,
        closableOnEscapeKey: false,
        centeredOnMobile: false,
      });
    },
    closeModal: ({ commit, state }) => {
      commit('closeModal');
      let $returnFocus;
      $returnFocus = document.getElementById(state.modal.returnFocusID);
      if ($returnFocus) {
        $returnFocus.focus();
      }
      if (state.modal.returnFocusID === 'title') {
        $returnFocus = document.getElementsByTagName('h1');
        $returnFocus[0].focus();
      }
    },
    /*
      tryAutoLogin

      Description: Try and access the 'cellbridge_session_token', if it exists then parse
                   the JWT and repopulate userInfo. If not then send the user to the login page.
    */
    tryAutoLogin({ commit, dispatch, state }) {
      const sessionToken = getCookie('cellbridge_session_token');

      if (sessionToken) {
        commit('setSessionToken', sessionToken);
        const jwt = parseJwt(sessionToken);
        dispatch('startSessionTimeout', jwt.exp);

        // Redirect to mobile app page if user is ineligible
        const isEligible = parseInt(getCookie('is_eligible'), 10);
        if (!isEligible) {
          commit(
            'updateDownloadMobileAppTitle',
            'It looks like your system is already up to date!',
          );

          if (router.currentRoute.name !== 'DownloadMobileApp') {
            router.push('/mobile-app');
          }
          return;
        }

        // Repopulate the store with information from cookies
        commit('updateUserLocations', jwt.sites);

        const hasMyADTCredentials = parseInt(getCookie('my_adt_user'), 10);
        if (hasMyADTCredentials) commit('updateHasMyADTCredentials', true);

        const manualTestRequired = parseInt(getCookie('manual_test'), 10);
        if (manualTestRequired) commit('updateManualTestRequired', true);

        const isDeviceTested = parseInt(getCookie('device_tested'), 10);
        if (isDeviceTested) {
          commit('updateDeviceTestStep', 'cbDeviceTestSuccess');
        } else {
          commit('updateDeviceTestStep', 'cbDeviceTestFetch');
        }

        const selectedSiteIndex = parseInt(getCookie('selected_site'), 10);

        // if the user has multiple sites and no location chosen, return them to login
        if (jwt.sites.length > 1 && !selectedSiteIndex && router.currentRoute !== 'Location') {
          dispatch('logout');
          router.push('/');
          return;
        }

        if (jwt.sites.length > 1) {
          commit('updateCurrentLocation', selectedSiteIndex);
        } else {
          commit('updateCurrentLocation', 0);
        }

        // Redirect user to the correct page
        if (isDeviceTested && router.currentRoute.name !== 'DeviceTest') {
          router.push('/test');
        } else if (!isDeviceTested && router.currentRoute.name !== 'SetupTest') {
          router.push('/setup-test');
        }

        if (!hasMyADTCredentials && !state.userInfo.telephoneNumber && !state.userInfo.pic) {
          commit('openModal', {
            modalContent: 'cbAuth',
            returnFocusID: 'title',
            closableOnEscapeKey: false,
            closableOnBackgroundClick: false,
            centeredOnMobile: true,
          });
        }
      }
    },
    /*
      startSessionTimeout

      Description: Sets a timeout for the session duration. When the timeout executes it
                   opens the session timeout modal and logs the user out

      Params:
        @exp - The end date of the session in Unix epoch time
    */
    startSessionTimeout({ commit, dispatch }, exp) {
      const timeoutDate = new Date(0);
      timeoutDate.setUTCSeconds(exp);
      const currentDate = new Date();
      const timeoutDuration = timeoutDate.getTime() - currentDate.getTime();

      if (currentDate < timeoutDate) {
        const sessionTimeout = setTimeout(() => {
          if (router.currentRoute.name !== 'Login' && router.currentRoute.name !== 'Register') {
            commit('openModal', {
              modalContent: 'cbSessionTimeout',
              closableOnBackgroundClick: false,
              closableOnEscapeKey: false,
              centeredOnMobile: true,
            });
          }
          dispatch('logout');
          commit('clearSessionTimeout');
        }, timeoutDuration);
        commit('setSessionTimeout', sessionTimeout);
      } else {
        dispatch('logout');
      }
    },
    // toggleLiveChat: ({ commit, state }) => {
    //   if (state.isLiveChatReady) {
    //     commit('setIsLiveChatOpen', !state.isLiveChatOpen);
    //     Pypestream('toggle');
    //   } else {
    //     commit('setIsLiveChatOpen', true);
    //     commit('setIsLiveChatReady');
    //     Pypestream(
    //       'boot',
    //       {
    //         APP_ID: process.env.VUE_APP_PYPESTREAM_APP_ID,
    //         domain: process.env.VUE_APP_PYPESTREAM_DOMAIN,
    //         env: process.env.VUE_APP_PYPESTREAM_ENV,
    //       },
    //       document.getElementById('pypestream-chat-container'),
    //     );
    //   }
    // },
    updateScreenSize: ({ commit }, payload) => {
      commit('updateScreenSize', payload);
    },
    updateFormErrorMessage: ({ commit }, message) => {
      commit('updateFormErrorMessage', message);
    },
    logout({ commit }) {
      deleteCookie('cellbridge_session_token');
      deleteCookie('my_adt_user');
      deleteCookie('manual_test');
      deleteCookie('device_tested');
      deleteCookie('is_eligible');
      commit('clearSessionTimeout');
      commit('updateLoginStep', 'cbLogin');
      commit('setSessionToken', null);
      commit('updateHasMyADTCredentials', false);
      commit('updateManualTestRequired', false);
    },

    /*---------------------
     * START: API CALLS
     *---------------------*/

    /*
      login

      Description: Calls the customer lookup API to get customer data.

      Params:
        @authData - An object with the authentication data, it either
                    has a username and password or telephone # and PIC.
    */
    login({ commit, dispatch, state }, authData) {
      commit('updateFormErrorMessage', '');
      commit('updateManualTestStep', '1');

      let params;

      if (authData.loginMethod === 'myADT') {
        params = {
          username: authData.username,
          password: authData.password,
        };
      } else {
        params = {
          telephoneNumber: authData.telephoneNumber,
          pic: authData.pic,
        };
        commit('updateUserInfo', params);
      }

      axios
        .post('/cb/customer-lookup', JSON.stringify(params), {
          headers: {
            'content-type': 'application/json',
          },
        })
        .then((res) => {
          let error = null;

          if (res?.data?.status === 'LOCKED') {
            error = new Error('Account is locked.');
            error.response = res;
            throw error;
          } else if (res?.data?.sites) {
            const result = res.data;
            if (authData.loginMethod === 'myADT') {
              commit('updateHasMyADTCredentials', true);
              setCookie('my_adt_user', 1, 0.5);
            } else {
              commit('updateHasMyADTCredentials', false);
              setCookie('my_adt_user', 0, 0.5);
            }

            if (authData.rememberLogin) {
              setCookie('username', authData.username, 720);
            } else {
              deleteCookie('username');
            }

            const jwt = parseJwt(result.jwt);
            commit('updateUserLocations', result.sites);
            dispatch('startSessionTimeout', jwt.exp);
            commit('setSessionToken', result.jwt);
            setCookie('cellbridge_session_token', result.jwt, 0.5);

            if (result.sites.length === 0) {
              commit(
                'updateDownloadMobileAppTitle',
                'It looks like your system is already up to date!',
              );
              setCookie('is_eligible', 0, 0.5);
              router.push('/mobile-app');
            } else if (state.userInfo.locations.length > 1) {
              setCookie('is_eligible', 1, 0.5);
              router.push('/location');
            } else {
              setCookie('is_eligible', 1, 0.5);
              commit('updateCurrentLocation', 0);
              dispatch('getDeviceStatus');
            }
          } else {
            error = new Error('Unhandled response when attempting to authenticate user.');
            error.response = res;
            throw error;
          }
        })
        .catch((error) => {
          let openErrorModal;
          let updateFormErrorMessage;
          if (error.response) {
            const accountLocked = error?.response?.data?.status === 'LOCKED' || error?.response?.data?.errorCode === 1001;
            const errorCode = error?.response?.data?.errorCode
                              || accountLocked ? 1001 : error.response.status;

            if (accountLocked === true) {
              openErrorModal = 'Your account has been temporarily locked.  Please try again later.';
            } else if (error.response.status === 401) {
              // no matches for username/password
              if (authData.loginMethod === 'myADT') updateFormErrorMessage = 'Your user name and password did not match our records. Please try again.';
              else updateFormErrorMessage = 'Phone number and Security Password not recognized.';
            } else openErrorModal = 'We encountered an error.';

            if (updateFormErrorMessage) commit('updateFormErrorMessage', updateFormErrorMessage);
            if (openErrorModal) {
              dispatch('openErrorModal', {
                body: openErrorModal,
                tryAgainDestination: '',
                showTroubleshoot: false,
                errorCode,
                // showLiveChat: false,
              });
            }
          }

          gtag('event', 'APIError', {
            event_label: `api error: ${error.message}`,
            event_category: 'customerLookupError',
            value: 1,
          });
        });
    },
    /*
      getDeviceStatus

      Description: Calls the status API and passes the CellBounce serial
                   number to see if the CellbBridge device is tested.
                   A tested device means that the setup is complete.
    */
    getDeviceStatus({ commit, dispatch, state }) {
      axios
        .get(`/cb/status/${state.userInfo.currentLocation.cellBounceSN}`, {
          headers: {
            Authorization: `Bearer ${state.userInfo.sessionToken}`,
          },
        })
        .then((res) => {
          const result = res.data;

          if (result) {
            if (result.status === 'tested') {
              commit('updateDeviceTestStep', 'cbDeviceTestSuccess');
              commit('updateManualTestRequired', false);
              setCookie('manual_test', 0, 0.5);
              setCookie('device_tested', 1, 0.5);
              if (router.currentRoute.name === 'DeviceTest') {
                dispatch('closeModal');
              } else {
                router.push('/test');
              }
            } else {
              commit('updateDeviceTestStep', 'cbDeviceTestFetch');
              commit('updateManualTestRequired', true);
              setCookie('manual_test', 1, 0.5);
              setCookie('device_tested', 0, 0.5);
              if (router.currentRoute.name === 'SetupTest') {
                dispatch('closeModal');
              } else {
                router.push('/setup-test');
              }
            }
          } else {
            gtag('event', 'APIError', {
              event_label: 'api error: failed to get device status',
              event_category: 'deviceStatusError',
              value: 1,
            });
            dispatch('openErrorModal', {
              body: 'We encountered an error.',
              tryAgainDestination: '',
              showTroubleshoot: false,
              // showLiveChat: false,
            });
          }
        })
        .catch((error) => {
          gtag('event', 'APIError', {
            event_label: `api error: ${error.message}`,
            event_category: 'deviceStatusError',
            value: 1,
          });
          dispatch('openErrorModal', {
            body: 'We encountered an error.',
            tryAgainDestination: '',
            showTroubleshoot: false,
            // showLiveChat: false,
          });
        });
    },
    /*
      initiateSignalTest

      Description: Calls the initiateSignalTest API and passes the CellBounce
                   serial number to start a manual signal test.
    */
    initiateSignalTest({ dispatch, state }) {
      axios
        .get(`/cb/initiateSignalTest/${state.userInfo.currentLocation.cellBounceSN}`, {
          headers: {
            Authorization: `Bearer ${state.userInfo.sessionToken}`,
          },
        })
        .then((res) => {
          const result = res.data;

          if (result && result.status === 'OK') {
            router.push('/test');
          } else {
            gtag('event', 'APIError', {
              event_label: 'api error: failed to initiate signal test',
              event_category: 'initiateSignalTestError',
              value: 1,
            });
            dispatch('openErrorModal', {
              body: 'We encountered an error. Please disarm your system and try again.',
              tryAgainDestination: '/setup-test',
              showTroubleshoot: false,
              // showLiveChat: false,
            });
          }
        })
        .catch((error) => {
          gtag('event', 'APIError', {
            event_label: `api error: ${error.message}`,
            event_category: 'initiateSignalTestError',
            value: 1,
          });
          dispatch('openErrorModal', {
            body: 'We encountered an error. Please disarm your system and try again.',
            tryAgainDestination: '/setup-test',
            showTroubleshoot: false,
            // showLiveChat: false,
          });
        });
    },
    /*
      putDeviceOnTest

      Description: Calls the test API and passes a site number to puts the device on test mode.
    */
    putDeviceOnTest({ dispatch, commit, state }) {
      axios
        .put(
          `/cb/test/${state.userInfo.currentLocation.site}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${state.userInfo.sessionToken}`,
            },
          },
        )
        .then((res) => {
          const result = res.data;
          if (result && result.status === 'ok') {
            commit('updateManualTestStep', '2');
          } else {
            gtag('event', 'APIError', {
              event_label: 'api error: failed to put device on test',
              event_category: 'putDeviceOnTestError',
              value: 1,
            });
            dispatch('openErrorModal', {
              body: 'We encountered an error. Please disarm your system and try again.',
              tryAgainDestination: '',
              showTroubleshoot: false,
              // showLiveChat: false,
            });
          }
        })
        .catch((error) => {
          gtag('event', 'APIError', {
            event_label: `api error: ${error.message}`,
            event_category: 'putDeviceOnTestError',
            value: 1,
          });
          dispatch('openErrorModal', {
            body: 'We encountered an error. Please disarm your system and try again.',
            tryAgainDestination: '',
            showTroubleshoot: false,
            // showLiveChat: false,
          });
        });
    },
    /*
      takeDeviceOffTest

      Description: Calls the test API and passes a site number to take the device off test mode.
    */
    takeDeviceOffTest({ state }) {
      axios
        .delete(`/cb/test/${state.userInfo.currentLocation.site}`, {
          headers: {
            Authorization: `Bearer ${state.userInfo.sessionToken}`,
          },
        })
        .catch((error) => {
          gtag('event', 'APIError', {
            event_label: `api error: ${error.message}`,
            event_category: 'takeDeviceOffTestError',
            value: 1,
          });
        });
    },
    /*
      pollDeviceStatus

      Description: Recursivly calls itself to poll the status API, uses a timeout in between calls.
                   The interval and timeout times can be configured in the .env files.

      Params:
        @timeout - A number in milliseconds that determines the time to
                   wait before checking the device status again.
    */
    pollDeviceStatus({ commit, dispatch, state }, timeout) {
      const pollingTimeout = setTimeout(() => {
        axios
          .get(`/cb/status/${state.userInfo.currentLocation.cellBounceSN}`, {
            headers: {
              Authorization: `Bearer ${state.userInfo.sessionToken}`,
            },
          })
          .then((res) => {
            commit('setPollingCounter', state.pollingCounter + 1);
            const currentTime = new Date();
            const currentTestDuration = (currentTime - state.deviceTestStartTime) / 1000;

            if (res.data && res.data.status === 'tested') {
              setCookie('device_tested', 1, 0.5);
              commit('setPollingCounter', -1);
            } else if (currentTestDuration < process.env.VUE_APP_POLLING_OVERALL_TIME) {
              dispatch('pollDeviceStatus', process.env.VUE_APP_POLLING_INTERVAL_TIME);
            } else {
              gtag('event', 'pollingTimeout', {
                event_label: 'test timed out',
                event_category: 'deviceTest',
                value: 1,
              });
              commit('updateTroubleshootPage', {
                buttonText: 'Try Again',
              });
              commit('setTestFailed');
              dispatch('openErrorModal', {
                body: 'Disarm your system to stop the siren from sounding',
                tryAgainDestination: '/setup-test',
                showTroubleshoot: true,
                // showLiveChat: true,
              });
            }
          })
          .catch((error) => {
            gtag('event', 'APIError', {
              event_label: `api error: ${error.message}`,
              event_category: 'deviceStatusError',
              value: 1,
            });
            commit('updateTroubleshootPage', {
              buttonText: 'Try Again',
            });
            dispatch('openErrorModal', {
              body: 'Disarm your system to stop the siren from sounding',
              tryAgainDestination: '/setup-test',
              showTroubleshoot: true,
              // showLiveChat: true,
            });
          });
      }, timeout * 1000);
      commit('setPollingTimeout', pollingTimeout);
    },
    /*
      registerUser

      Description: Calls the register API to sign a customer up for myADT.

      Params:
        @formData - An object with form data
    */
    registerUser({ state, commit }, formData) {
      commit('updateFormErrorMessage', '');
      commit('setRequestInProgress', true);
      const params = { ...formData };
      params.sitePhone = state.userInfo.telephoneNumber;
      params.securityPassword = state.userInfo.pic;
      params.confirmEmailAddress = params.emailAddress;
      params.uuid = '';

      axios
        .post('/customer/registration', JSON.stringify(params), {
          headers: {
            Authorization: `Bearer ${state.userInfo.sessionToken}`,
            'content-type': 'application/json',
          },
        })
        .then((res) => {
          commit('setRequestInProgress', false);
          const result = res.data;

          if (result && result.status === 200) {
            commit('updateDownloadMobileAppTitle', 'Thanks for registering. Now, get more!');
            router.push('/mobile-app');
          } else {
            gtag('event', 'APIError', {
              event_label: 'api error: failed to register user',
              event_category: 'registerUserError',
              value: 1,
            });
            router.push('/error');
          }
        })
        .catch((error) => {
          commit('setRequestInProgress', false);
          if (error.response.data[0] === 'Registration.errorUserAlreadyExists') {
            commit(
              'updateFormErrorMessage',
              'The user email you provided is already in use. Please go to <a class="cb-link" href="https://myadt.com/reset-password">myADT.com</a> if you need to retrieve your login information.',
            );
          } else {
            router.push('/error');
          }
          gtag('event', 'APIError', {
            event_label: `api error: ${error.message}`,
            event_category: 'registerUserError',
            value: 1,
          });
        });
    },

    /*---------------------
     * END: API CALLS
     *---------------------*/
  },
});
